/*
  CBG Product Page components.

  Defines:
   - <cbg-product-gallery> — lazy-loaded Swiper image gallery (main + thumbnails + bullets),
     mirroring the assets/cbg-hero-slider.js load pattern.
   - <cbg-quantity>       — quantity control: keyboard entry, mobile +/- buttons, minimum clamp
                            + validation with an inline error.
   - <cbg-product>        — orchestrator: intercepts the product form, enforces the minimum,
                            adds to cart via AJAX, opens the "Added to cart" popup, keeps the
                            existing cbg-mini-cart in sync by publishing cartUpdate, and drives
                            the tabs + mobile Overview slide-out.

  Relies on Dawn globals already on the page (loaded before this deferred script):
  routes, PUB_SUB_EVENTS, publish. All are referenced defensively.
*/

/* ---------- Shared Swiper loader (one fetch across all cbg sliders on the page) ---------- */
function cbgLoadSwiper(jsUrl, cssUrl) {
  if (window.Swiper) return Promise.resolve();
  if (!window.__cbgSwiperPromise) {
    window.__cbgSwiperPromise = new Promise((resolve, reject) => {
      let pending = 2;
      const done = () => {
        pending -= 1;
        if (pending === 0) resolve();
      };
      const fail = () => {
        window.__cbgSwiperPromise = null;
        reject(new Error('Swiper failed to load'));
      };

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      link.onload = done;
      link.onerror = fail;
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src = jsUrl;
      script.onload = done;
      script.onerror = fail;
      document.head.appendChild(script);
    });
  }
  return window.__cbgSwiperPromise;
}

/* ============================= GALLERY ============================= */
if (!customElements.get('cbg-product-gallery')) {
  class CbgProductGallery extends HTMLElement {
    connectedCallback() {
      this.swiperEl = this.querySelector('.cbg-pdp-gallery__swiper');
      this.thumbsEl = this.querySelector('.cbg-pdp-gallery__thumbs');
      this.slides = this.querySelectorAll('.cbg-pdp-gallery__slide');

      this.setupZoom(); /* desktop hover-zoom — works with or without Swiper (single-image too) */

      if (!this.swiperEl || this.slides.length <= 1) return; /* single image: nothing to init */

      const root = this.closest('cbg-product') || this;
      this.jsUrl = root.dataset.swiperJs;
      this.cssUrl = root.dataset.swiperCss;

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.observer.disconnect();
            this.observer = null;
            this.init();
          }
        },
        { rootMargin: '400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.teardownZoom();
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.thumbsSwiper) {
        this.thumbsSwiper.destroy(true, false);
        this.thumbsSwiper = null;
      }
      if (this.swiper) {
        this.swiper.destroy(true, false);
        this.swiper = null;
      }
    }

    /* ---- Desktop hover-zoom ----
       Scales the hovered image in place; transform-origin tracks the cursor so the
       zoomed-in area shifts with the mouse (corners show their corner). The image box
       and the Swiper never change size — the swiper's overflow:hidden clips the overflow.
       Gated to fine-pointer desktop widths; touch devices never trigger it. */
    setupZoom() {
      if (this.dataset.zoom !== 'true') return;
      this.zoomImages = this.querySelectorAll('.cbg-pdp-gallery__image');
      if (!this.zoomImages.length) return;

      this.zoomMQ = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
      this.onZoomMove = this.onZoomMove.bind(this);
      this.onZoomLeave = this.onZoomLeave.bind(this);

      this.zoomImages.forEach((img) => {
        img.addEventListener('mousemove', this.onZoomMove);
        img.addEventListener('mouseleave', this.onZoomLeave);
      });
    }

    onZoomMove(event) {
      if (!this.zoomMQ || !this.zoomMQ.matches) return;
      const img = event.currentTarget;
      const rect = img.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = x + '% ' + y + '%';
      img.classList.add('cbg-pdp-gallery__image--zoom');
    }

    onZoomLeave(event) {
      const img = event.currentTarget;
      img.classList.remove('cbg-pdp-gallery__image--zoom');
      img.style.transformOrigin = '';
    }

    teardownZoom() {
      if (!this.zoomImages) return;
      this.zoomImages.forEach((img) => {
        img.removeEventListener('mousemove', this.onZoomMove);
        img.removeEventListener('mouseleave', this.onZoomLeave);
        img.classList.remove('cbg-pdp-gallery__image--zoom');
        img.style.transformOrigin = '';
      });
      this.zoomImages = null;
    }

    async init() {
      try {
        await cbgLoadSwiper(this.jsUrl, this.cssUrl);
      } catch (error) {
        console.warn('cbg-product-gallery:', error.message);
        return; /* first slide stays visible via pre-init CSS */
      }
      if (!this.isConnected || this.swiper) return;

      if (this.thumbsEl) {
        this.thumbsSwiper = new window.Swiper(this.thumbsEl, {
          slidesPerView: 'auto',
          spaceBetween: 8,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
        });
      }

      this.swiper = new window.Swiper(this.swiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        watchOverflow: true,
        navigation: {
          prevEl: this.querySelector('.cbg-pdp-gallery__arrow--prev'),
          nextEl: this.querySelector('.cbg-pdp-gallery__arrow--next'),
        },
        pagination: {
          el: this.querySelector('.cbg-pdp-gallery__pagination'),
          clickable: true,
        },
        thumbs: this.thumbsSwiper ? { swiper: this.thumbsSwiper } : undefined,
      });
    }
  }
  customElements.define('cbg-product-gallery', CbgProductGallery);
}

/* ============================= QUANTITY ============================= */
if (!customElements.get('cbg-quantity')) {
  class CbgQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-cbg-qty-input]');
      this.errorEl = this.querySelector('[data-cbg-qty-error]');
      this.min = parseInt(this.dataset.min, 10) || 1;
      if (!this.input) return;

      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.addEventListener('click', this.onClick);
      this.input.addEventListener('input', this.onInput);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.input) this.input.removeEventListener('input', this.onInput);
    }

    onClick(event) {
      const btn = event.target.closest('[data-cbg-qty-plus], [data-cbg-qty-minus]');
      if (!btn) return;
      const step = btn.hasAttribute('data-cbg-qty-plus') ? 1 : -1;
      const current = parseInt(this.input.value, 10) || this.min;
      this.setValue(current + step);
    }

    onInput() {
      /* clear the error as soon as the user edits; validation happens again on add */
      if (this.errorEl && !this.errorEl.hidden) this.clearError();
    }

    setValue(value) {
      const next = Math.max(this.min, value);
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    get value() {
      return parseInt(this.input.value, 10) || 0;
    }

    /* Returns true if valid; otherwise shows the error and returns false */
    validate(message) {
      if (this.value < this.min) {
        this.showError(message);
        return false;
      }
      this.clearError();
      return true;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.input.removeAttribute('aria-invalid');
    }
  }
  customElements.define('cbg-quantity', CbgQuantity);
}

/* ---------- Rich-text JSON → HTML (Shopify rich_text_field schema) ----------
   Fallback for when custom.details is defined as a JSON/text metafield: the Liquid drop
   prints the raw rich-text JSON, which we convert to HTML here. Safe no-op for real HTML
   (JSON.parse throws → content left untouched). */
function cbgEscapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cbgRteNodeToHtml(node) {
  if (!node) return '';
  if (Array.isArray(node)) return node.map(cbgRteNodeToHtml).join('');
  const kids = () => (node.children ? cbgRteNodeToHtml(node.children) : '');
  switch (node.type) {
    case 'root':
      return kids();
    case 'paragraph':
      return '<p>' + kids() + '</p>';
    case 'heading': {
      const level = Math.min(Math.max(parseInt(node.level, 10) || 2, 1), 6);
      return '<h' + level + '>' + kids() + '</h' + level + '>';
    }
    case 'list': {
      const tag = node.listType === 'ordered' ? 'ol' : 'ul';
      return '<' + tag + '>' + kids() + '</' + tag + '>';
    }
    case 'list-item':
      return '<li>' + kids() + '</li>';
    case 'link': {
      const url = cbgEscapeHtml(node.url || '#');
      const target = node.target ? ' target="' + cbgEscapeHtml(node.target) + '" rel="noopener"' : '';
      const title = node.title ? ' title="' + cbgEscapeHtml(node.title) + '"' : '';
      return '<a href="' + url + '"' + target + title + '>' + kids() + '</a>';
    }
    case 'text': {
      let text = cbgEscapeHtml(node.value || '');
      if (node.bold) text = '<strong>' + text + '</strong>';
      if (node.italic) text = '<em>' + text + '</em>';
      return text;
    }
    default:
      return kids();
  }
}

function cbgRenderRichText(el) {
  if (!el) return;
  const raw = el.textContent.trim();
  if (!raw.startsWith('{') || raw.indexOf('"type"') === -1) return; /* already HTML */
  let data;
  try {
    data = JSON.parse(raw);
  } catch (error) {
    return; /* not rich-text JSON — leave as-is */
  }
  if (!data || data.type !== 'root') return;
  el.innerHTML = cbgRteNodeToHtml(data);
}

/* ============================= ORCHESTRATOR ============================= */
if (!customElements.get('cbg-product')) {
  class CbgProduct extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.cbg-pdp__form');
      this.quantity = this.querySelector('cbg-quantity');
      this.popup = this.querySelector('[data-cbg-popup]');
      this.overview = this.querySelector('[data-cbg-overview]');
      this.minError = this.dataset.minError || 'Please increase the quantity.';
      this.addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      this.lastFocus = null;

      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);

      /* Render rich-text Details from JSON if the metafield wasn't a true Rich text field */
      this.querySelectorAll('[data-cbg-rte]').forEach(cbgRenderRichText);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* ---- Add to cart ---- */
    async onSubmit(event) {
      event.preventDefault();
      if (this.quantity && !this.quantity.validate(this.minError)) return;

      const buttons = this.querySelectorAll('[data-cbg-add]');
      buttons.forEach((b) => b.setAttribute('aria-disabled', 'true'));

      try {
        const formData = new FormData(this.form);
        const response = await fetch(this.addUrl, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();

        if (data.status) {
          /* Shopify error payload (e.g. sold out / min rule) */
          console.warn('cbg-product add error:', data.description || data.message);
          this.notifyError(data);
          return;
        }

        this.openPopup(this.quantity ? this.quantity.value : 1);
        this.notifyUpdate(data);
      } catch (error) {
        console.warn('cbg-product add failed:', error);
      } finally {
        buttons.forEach((b) => b.removeAttribute('aria-disabled'));
      }
    }

    notifyUpdate(cartData) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cbg-pdp', cartData });
      }
    }

    notifyError(data) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, { source: 'cbg-pdp', message: data.description || data.message });
      }
    }

    /* ---- Click delegation: popup close, overview open/close, tabs ---- */
    onClick(event) {
      /* Close on the X / "continue" buttons, or when the click lands on the
         dim wrapper itself (outside the window). Clicks inside the window keep
         it open — the dialog is a child, so it never matches the catcher. */
      if (
        event.target.closest('[data-cbg-popup-close]') ||
        (event.target === this.popup && event.target.hasAttribute('data-cbg-popup-catcher'))
      ) {
        this.closePopup();
        return;
      }
      if (event.target.closest('[data-cbg-overview-open]')) {
        this.openOverview();
        return;
      }
      if (event.target.closest('[data-cbg-overview-close]')) {
        this.closeOverview();
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.popup && this.popup.classList.contains('is-open')) this.closePopup();
      else if (this.overview && this.overview.classList.contains('is-open')) this.closeOverview();
    }

    /* ---- Popup ---- */
    openPopup(qty) {
      if (!this.popup) return;
      const qtyEl = this.popup.querySelector('[data-cbg-popup-qty]');
      if (qtyEl) qtyEl.textContent = qty;
      this.lastFocus = document.activeElement;
      this.popup.classList.add('is-open');
      this.popup.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      const closeBtn = this.popup.querySelector('[data-cbg-popup-close]');
      if (closeBtn) closeBtn.focus();
    }

    closePopup() {
      if (!this.popup || !this.popup.classList.contains('is-open')) return;
      this.popup.classList.remove('is-open');
      this.popup.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
    }

    /* ---- Mobile Overview slide-out ---- */
    openOverview() {
      if (!this.overview) return;
      this.overview.classList.add('is-open');
      this.overview.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
    }

    closeOverview() {
      if (!this.overview) return;
      this.overview.classList.remove('is-open');
      this.overview.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
    }
  }
  customElements.define('cbg-product', CbgProduct);
}

/* ============================= WISHLIST ============================= */
/*
  Bridge for the third-party "Wishlist by Square" app.

  The PDP has a custom, brand-styled "Add to Wishlist" button, but the wishlist itself is powered by
  the app, which injects its OWN button (.Vtl-WishlistButtonWrapper) via automatic placement. The app
  can't adopt our button.

  We do NOT simulate clicks (synthetic events are isTrusted:false — the app fires its analytics but can
  skip the real save / login popup). Instead we OVERLAY the app's real, invisible button exactly on top
  of ours, so a genuine, trusted user click lands on the app element itself. Our styled button shows
  underneath; the app does all the work; the app's own label/state is mirrored back onto our button.

  DEPENDENCY: the app must stay enabled in a product-page placement, or there is nothing to overlay.
  If the app changes its .Vtl-* markup, update the selectors below.
*/
if (!customElements.get('cbg-wishlist')) {
  const APP_WRAPPER = '.Vtl-WishlistButtonWrapper';

  class CbgWishlist extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-cbg-wishlist]');
      if (!this.btn) return;
      this.labelAdd = this.btn.dataset.labelAdd || this.btn.textContent.trim();
      this.labelAdded = this.btn.dataset.labelAdded || this.labelAdd;
      this.reposition = this.reposition.bind(this);

      /* Logged out: the wishlist needs an account. Send the visitor to the login page instead of
         overlaying the app button (whose own click would open the app's login popup). The presence
         of data-login-url is the "not logged in" signal (Liquid only renders it for guests). */
      this.loginUrl = this.dataset.loginUrl;
      if (this.loginUrl) {
        this.onLoginClick = this.onLoginClick.bind(this);
        this.btn.addEventListener('click', this.onLoginClick);
        this.hideApp();
        return;
      }

      this.findApp();
    }

    disconnectedCallback() {
      if (this.waitObserver) this.waitObserver.disconnect();
      if (this.stateObserver) this.stateObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onLoginClick) this.btn.removeEventListener('click', this.onLoginClick);
      window.removeEventListener('scroll', this.reposition, true);
      window.removeEventListener('resize', this.reposition);
      if (this.appWrapper && this.onOverlayEnter) {
        this.appWrapper.removeEventListener('mouseenter', this.onOverlayEnter);
        this.appWrapper.removeEventListener('mouseleave', this.onOverlayLeave);
      }
    }

    onLoginClick(e) {
      e.preventDefault();
      window.location.href = this.loginUrl;
    }

    /* Guests don't get the overlay, so hide the app's auto-placed button to avoid a stray/duplicate
       wishlist control appearing on the page. The button may be injected late, so watch for it too. */
    hideApp() {
      const existing = document.querySelector(APP_WRAPPER);
      if (existing) {
        existing.classList.add('cbg-wishlist-hidden');
        return;
      }
      this.waitObserver = new MutationObserver(() => {
        const el = document.querySelector(APP_WRAPPER);
        if (el) {
          this.waitObserver.disconnect();
          el.classList.add('cbg-wishlist-hidden');
        }
      });
      this.waitObserver.observe(document.body, { childList: true, subtree: true });
    }

    findApp() {
      const existing = document.querySelector(APP_WRAPPER);
      if (existing) {
        this.bindApp(existing);
        return;
      }
      this.waitObserver = new MutationObserver(() => {
        const el = document.querySelector(APP_WRAPPER);
        if (el) {
          this.waitObserver.disconnect();
          this.bindApp(el);
        }
      });
      this.waitObserver.observe(document.body, { childList: true, subtree: true });
    }

    bindApp(wrapper) {
      this.appWrapper = wrapper;
      wrapper.classList.add('cbg-wishlist-overlay');
      this.reposition();
      /* Keep the overlay glued to our button through scroll / resize / layout changes. */
      window.addEventListener('scroll', this.reposition, true);
      window.addEventListener('resize', this.reposition);
      /* The overlay steals the pointer, so native :hover never reaches our button. Mirror the
         hover state onto it manually. */
      this.onOverlayEnter = () => this.btn.classList.add('is-hover');
      this.onOverlayLeave = () => this.btn.classList.remove('is-hover');
      wrapper.addEventListener('mouseenter', this.onOverlayEnter);
      wrapper.addEventListener('mouseleave', this.onOverlayLeave);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.reposition);
        this.resizeObserver.observe(this.btn);
      }
      /* Mirror the app's own label + state onto our button (also re-position, size can change). */
      this.syncState();
      this.stateObserver = new MutationObserver(() => {
        this.reposition();
        this.syncState();
      });
      this.stateObserver.observe(wrapper, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
    }

    reposition() {
      if (!this.appWrapper) return;
      const r = this.btn.getBoundingClientRect();
      const s = this.appWrapper.style;
      s.top = r.top + 'px';
      s.left = r.left + 'px';
      s.width = r.width + 'px';
      s.height = r.height + 'px';
    }

    syncState() {
      if (!this.appWrapper) return;
      /* The app flips a modifier on .Vtl-WishlistButton (e.g. --active) once the product is saved. */
      const inner = this.appWrapper.querySelector('.Vtl-WishlistButton');
      const textEl = this.appWrapper.querySelector('[class*="WishlistButton__"][class*="Text"]');
      const label = textEl ? textEl.textContent.trim() : '';
      const active = inner ? /active|added|remove/i.test(inner.className) : false;
      if (label) this.btn.textContent = label;
      this.btn.classList.toggle('is-active', active);
      this.btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }
  customElements.define('cbg-wishlist', CbgWishlist);
}
